/**
 * Browser entry point. The page opens on its loading screen; everything
 * the world needs is downloaded and put on the GPU behind it. Then the
 * lobby: a newcomer picks a champion and plays as a guest; anyone with a
 * session (a guest's too) picks or creates a character on the character
 * select; logging in or creating an account keeps a guest's characters.
 *
 * URL options:
 *   ?net=good|typical|bad          simulated network preset (see NETWORK_PRESETS)
 *   ?lag=150&jitter=20&loss=0.05   custom conditions (RTT ms, jitter ms, loss 0..1); override the preset
 *   ?debug=1                       open the netcode overlay (toggle with F3)
 *   ?quality=low                   small shadows, no bloom or grading, one pixel per pixel: for slow
 *                                  machines and software rendering (tools/play.mjs --fast)
 *   ?server=ws://host:8080/ws      custom server
 *   ?champion=frostbinder          skip the lobby and play a guest (not saved) of that champion,
 *   ?name=Alice                    with this name (for testing: pyromancer | frostbinder | archer | siren)
 */

import { FrameLoop, Input, Renderer, WebSocketClientTransport, WorldLabels } from '../../engine/client/index.ts';
import { ConditionedTransport, type ClientTransport } from '../../engine/net/index.ts';
import { NETWORK_PRESETS, type NetworkPresetName } from '../shared/network-presets.ts';
import { DebugOverlay } from './debug-overlay.ts';
import { HOME_MAP, mapDef, type MapDef } from '../shared/maps.ts';
import type { TerrainSpec } from '../../engine/client/index.ts';
import { Hud } from './hud.ts';
import { BINDINGS, type GameAction } from './controls.ts';
import { ShooterClient } from './shooter-client.ts';
import { loadPlayerBody } from './art/player.ts';
import { loadTerrainLayers, TERRAIN_SETS, terrainFor, type TerrainLayers } from './art/terrain.ts';
import { MAP_LOOKS } from './art/maps.ts';
import { loadProps } from './art/props.ts';
import { loadCreeps } from './art/creeps.ts';
import { loadNpcs } from './art/npcs.ts';
import { loadBoss } from './art/boss.ts';
import { loadMartichora } from './art/martichora.ts';
import { loadQueen } from './art/queen.ts';
import { isChampionId } from '../shared/champions.ts';
import { AccountApi } from './account-api.ts';
import { meshAssets, upload } from './preload.ts';
import { CharacterPreview } from './ui/character-preview.ts';
import { Portrait } from './ui/portrait.ts';
import { DamageNumbers } from './ui/damage-numbers.ts';
import { ChatBox } from './ui/chat.ts';
import { Minimap } from './ui/minimap.ts';
import { LoadingScreen } from './ui/loading-screen.ts';
import { GameMenu } from './ui/game-menu.ts';
import { Lobby } from './ui/lobby.ts';
import { ClientTelemetry } from './telemetry.ts';
import { ReconnectingNotice } from './ui/reconnecting.ts';
import { SoundSettings } from './ui/sound-settings.ts';
import { createAudio } from './sounds.ts';

const params = new URLSearchParams(location.search);
/** Why we came back to the lobby (set before a reload), shown on it once. */
const NOTICE_KEY = 'shooter.notice';
/** The character to go straight back into after the page reloaded itself for a new build. */
const RESUME_KEY = 'shooter.resume';
/** Open the lobby on creating an account (a guest asked to keep their character, from the game's menu). */
const REGISTER_KEY = 'shooter.register';

/** Reads and clears a once-only value from sessionStorage ('' if none, or no storage). */
function takeOnce(key: string): string {
  try {
    const value = sessionStorage.getItem(key) ?? '';
    sessionStorage.removeItem(key);
    return value;
  } catch {
    return '';
  }
}

function createTransport(): ClientTransport {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = params.get('server') ?? `${scheme}://${location.host}/ws`;
  const transport = new WebSocketClientTransport(url);
  const preset = NETWORK_PRESETS[(params.get('net') ?? '') as NetworkPresetName];
  const latencyMs = Number(params.get('lag') ?? preset?.latencyMs ?? 0);
  const jitterMs = Number(params.get('jitter') ?? preset?.jitterMs ?? 0);
  const loss = Number(params.get('loss') ?? preset?.loss ?? 0);
  if (latencyMs <= 0 && jitterMs <= 0 && loss <= 0) return transport;
  return new ConditionedTransport(transport, { latencyMs, jitterMs, loss });
}

function createLabel(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'label';
  el.innerHTML = '<span></span><div class="hp"><i></i></div>';
  return el;
}

const canvas = document.getElementById('game');
const labelLayer = document.getElementById('labels');
if (!(canvas instanceof HTMLCanvasElement) || !labelLayer) throw new Error('index.html is missing #game or #labels');

const lowQuality = params.get('quality') === 'low';
// Errors and performance go to the server (telemetry.ts); the map, ping and renderer join in once we're in the world.
const telemetry = new ClientTelemetry(() => ({ map: null, ping: 0, renderer: null, lowQuality }));
telemetry.listen();
telemetry.watchCanvas(canvas);

const input = new Input<GameAction>(canvas, BINDINGS);

// Sound starts on the first click or key press (browsers allow nothing sooner); every button clicks.
let storage: Storage | null = null;
try { storage = localStorage; } catch { /* volumes aren't kept */ }
const audio = createAudio(storage);
audio.listenForUnlock(window);
addEventListener('click', (e) => {
  if (e.target instanceof Element && e.target.closest('button')) audio.play('ui-click');
}, true);

const damageLayer = document.getElementById('damage');
if (!damageLayer) throw new Error('index.html is missing #damage');

const debugEl = document.getElementById('debug');
if (!debugEl) throw new Error('index.html is missing #debug');

const loading = new LoadingScreen(document.getElementById('loading')!, (message) => telemetry.error(message, 'loading'));
addEventListener('unhandledrejection', (e) => loading.fail(`Something went wrong: ${String(e.reason)}. Reload the page to try again.`));

// Everything downloads at once, behind the loading screen.
const playerBodyLoad = loading.track('Gathering the champions', loadPlayerBody(), 2);
const home = mapDef(HOME_MAP);
const terrainLoad = loading.track('Painting the land', loadTerrainLayers(), 2);
const propsLoad = loading.track('Raising Hollowmere', loadProps(), 2);
const creepsLoad = loading.track('Waking the wilds', loadCreeps(), 2);
const npcsLoad = loading.track('Calling the villagers', loadNpcs());
const bossLoad = loading.track('Stirring the Warden', loadBoss());
const queenLoad = loading.track('Waking the Hollow Queen', loadQueen());
const martichoraLoad = loading.track('Waking the Martichora', loadMartichora());
void loading.track('Inking the scrolls', document.fonts.ready);

let renderer: Renderer;
try {
  // An enchanted late afternoon: golden sun, violet sky light and rim, a lavender haze, and a grade that
  // cools the shadows and warms the lights. Bloom reaches down a little, so magic and fire glow.
  renderer = new Renderer(canvas, {
    // Each map has its own (art/maps.ts); the client switches them as we travel.
    lighting: { ...MAP_LOOKS[home.id].lighting },
    ...(lowQuality ? { shadowMapSize: 512, maxPixelRatio: 1 } : {}),
    post: lowQuality ? false : {
      bloom: 0.8,
      bloomThreshold: 0.85,
      saturation: 1.14,
      shadowTint: [0.94, 0.96, 1.08],
      highlightTint: [1.06, 1, 0.9],
    },
  });
} catch (error) {
  loading.fail(`${error instanceof Error ? error.message : String(error)}. Hollowmere needs WebGL 2.`);
  throw error;
}
const hud = new Hud();
const preview = new CharacterPreview(hud.dollCanvas);
const portrait = new Portrait(hud.portraitCanvas);

const [playerBody, ground, props, creeps, npcs, boss, queen, martichora] = await Promise.all([
  playerBodyLoad, terrainLoad, propsLoad, creepsLoad, npcsLoad, bossLoad, queenLoad, martichoraLoad,
]);
/**
 * Each map's ground, painted the first time it's needed. The snowy set
 * downloads after the loading screen, behind play: until it's in, a snowy
 * map gets the plain set (not kept), and the client repaints once it lands.
 */
const terrains = new Map<string, TerrainSpec>();
let snowGround: TerrainLayers | null = null;
const terrainOf = (map: MapDef): TerrainSpec => {
  let spec = terrains.get(map.id);
  if (spec) return spec;
  if (map.level.ground === 'snow') {
    if (!snowGround) return terrainFor(ground, map.level);
    spec = terrainFor(snowGround, map.level, 'snow');
  } else {
    spec = terrainFor(ground, map.level);
  }
  terrains.set(map.id, spec);
  return spec;
};
const snowLoad = loadTerrainLayers(TERRAIN_SETS.snow.url).then((layers) => { snowGround = layers; });
// Onto the GPU now, not the first time each is seen.
await loading.step('Preparing the world', () => {
  renderer.setTerrain(terrainOf(home));
  upload(renderer, meshAssets(playerBody, props, creeps, npcs, boss, queen, martichora));
}, 2);
await loading.step('Polishing the gear', () => {
  upload(preview.renderer, meshAssets(playerBody));
  upload(portrait.renderer, meshAssets(playerBody));
});

const guest = params.get('champion');
let login: { session: string; character: number } | undefined;
let guestAccount = false;
let champion = isChampionId(guest) ? guest : 'pyromancer' as const;
let playerName = params.get('name') ?? '';
const api = new AccountApi();
if (!isChampionId(guest)) {
  loading.hide();
  const notice = takeOnce(NOTICE_KEY);
  const resume = Number(takeOnce(RESUME_KEY)) || null;
  const start = takeOnce(REGISTER_KEY) ? 'register' : 'auto';
  const choice = await new Lobby(document.getElementById('lobby')!, api, preview, hud.dollCanvas, playerBody).run(notice, resume, start);
  guestAccount = choice.guest;
  // The preview's canvas goes back to the character panel.
  document.getElementById('doll')!.append(hud.dollCanvas);
  login = { session: choice.session, character: choice.character.id };
  champion = choice.character.champion;
  playerName = choice.character.name;
} else {
  loading.hide();
}

/** Back to the lobby (the character select, or the login once logged out), keeping the other URL options. */
function toLobby(): void {
  const url = new URL(location.href);
  url.searchParams.delete('champion');
  url.searchParams.delete('name');
  location.assign(url);
}

const transport = createTransport();
/** We are leaving the world on purpose (no "Reconnecting…" then). */
let leaving = false;
const menu = new GameMenu(document.getElementById('menu')!, login ? {
  // Leaving closes the connection first: the server saves the character as it goes.
  switchCharacter: () => {
    leaving = true;
    transport.disconnect();
    toLobby();
  },
  logOut: () => {
    leaving = true;
    transport.disconnect();
    void api.logout().catch(() => {}).finally(toLobby);
  },
  // A guest: back to the lobby to create an account, which keeps the character (saved as we leave).
  ...(guestAccount ? {
    keep: () => {
      leaving = true;
      transport.disconnect();
      try { sessionStorage.setItem(REGISTER_KEY, '1'); } catch { /* the character select instead */ }
      toLobby();
    },
  } : {}),
} : null, toLobby);
new SoundSettings(document.getElementById('sound')!, audio);

const chat = new ChatBox(document.getElementById('chat')!);
hud.champion = champion;
const client = new ShooterClient({
  transport,
  renderer,
  input,
  hud,
  labels: new WorldLabels<number>(labelLayer, createLabel),
  damageNumbers: new DamageNumbers(damageLayer),
  debug: new DebugOverlay(debugEl, params.has('debug')),
  chat,
  minimap: new Minimap(document.getElementById('minimap') as HTMLCanvasElement, document.getElementById('worldmap')!, home),
  map: home,
  terrainOf,
  playerName,
  champion,
  playerBody,
  props,
  creeps,
  npcs,
  bosses: { warden: boss, queen, leucrocotta: creeps.beasts, martichora },
  preview,
  portrait,
  ...(login ? { login } : {}),
  audio,
  onMenu: () => menu.toggle(),
  menuOpen: () => menu.open,
});

// Refused (a session that expired, a character deleted elsewhere): back to the lobby, saying why.
client.session.onDenied.on((reason) => {
  try {
    sessionStorage.setItem(NOTICE_KEY, reason);
  } catch {
    // no storage: the lobby just won't say why
  }
  location.reload();
});

// A guest is reminded, now and then as they level, that an account keeps their character.
if (guestAccount) {
  client.session.onEvent.on(({ event }) => {
    if (event.type !== 'levelUp' || event.playerId !== client.session.playerId || ![3, 6, 10].includes(event.level)) return;
    chat.add('system', `You're playing as a guest. Create an account (Esc, then "Create an account") to keep ${playerName} and play from any browser.`);
  });
}

// The snowy ground came in: repaint it if we're on a snowy map already.
void snowLoad.then(() => { if (mapDef(client.mapId).level.ground === 'snow') client.refreshTerrain(); }, (error) => telemetry.error(String(error), 'loading'));

telemetry.context = () => ({ map: client.mapId, ping: client.session.stats.ping, renderer: renderer.stats, lowQuality });
// The server went away (a deploy restarts it): say so until it welcomes us back. The transport
// retries by itself, and the server puts the character back where it was saved.
const reconnecting = new ReconnectingNotice();
transport.onClose.on(() => {
  if (!leaving) reconnecting.show();
});
client.session.onWelcome.on(() => reconnecting.hide());

// Back on a server running a newer build than this page: reload, and (logged in) go straight
// back into the world with the same character rather than stopping at the character select.
client.session.onOutdated.on(() => {
  leaving = true;
  transport.disconnect();
  try {
    if (login) sessionStorage.setItem(RESUME_KEY, String(login.character));
  } catch {
    // no storage: the reload stops at the character select
  }
  location.reload();
});

client.start();
new FrameLoop((dt, now) => {
  client.frame(dt, now);
  telemetry.frame(dt);
}).start();

// Handy for debugging from the devtools console.
Object.assign(window, { shooter: client, renderer, audio });
